import React, { createContext, useContext, useState, useEffect } from 'react';
import { loginApi, logoutApi } from '../features/auth';
import { decodeToken, isTokenExpired } from '../utils/jwt';
import { getItem, removeItem } from '../utils/storage';

const AuthContext = createContext(null);

function buildUser(decoded, stored) {
  const username = decoded?.sub || stored?.username || '';
  const namePart = username.split('@')[0];
  const name = namePart.charAt(0).toUpperCase() + namePart.slice(1);
  const initials = namePart.slice(0, 2).toUpperCase();
  return {
    username,
    name,
    initials,
    userIdGuid: decoded?.userIdGuid || stored?.userIdGuid,
    sessionId: decoded?.sessionId,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: restore session from a valid stored token
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token && !isTokenExpired(token)) {
      const decoded = decodeToken(token);
      const stored  = getItem('virmatics_auth');
      setUser(buildUser(decoded, stored));
    } else if (token) {
      // Token exists but has expired — clear it
      localStorage.removeItem('accessToken');
      removeItem('virmatics_auth');
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    const result = await loginApi(username, password);
    if (result.success) {
      const token   = localStorage.getItem('accessToken');
      const decoded = decodeToken(token);
      const stored  = getItem('virmatics_auth');
      setUser(buildUser(decoded, stored));
    }
    return result;
  };

  const logout = async () => {
    await logoutApi();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
